import React, { useEffect, useState } from 'react';
import { Package, Tag, Hash } from 'lucide-react';
import api from '../../services/api';

const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtNum = n => Number(n).toLocaleString('en-IN');
const pct = (n, t) => t === 0 ? 0 : Math.round((Number(n) / Number(t)) * 100);

const Bar = ({ value, total, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ flex: 1, height: 6, background: '#F0DCC8', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{ width: `${pct(value, total)}%`, height: '100%', background: color, borderRadius: 99 }} />
    </div>
    <span style={{ fontSize: 11, color: '#9CA3AF', width: 28, textAlign: 'right' }}>{pct(value, total)}%</span>
  </div>
);

const OrderSection = ({ title, icon: Icon, color, bg, border, rows, idLabel }) => {
  if (!rows?.length) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: bg, color, border: `1px solid ${border}` }}>
          <Icon size={13} /> {title}
        </span>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>{rows.length} group{rows.length !== 1 ? 's' : ''}</span>
        <div style={{ flex: 1, height: 1, background: '#F0DCC8' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
        {rows.map(row => (
          <div key={row.order_id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '13px 16px', background: '#FFF8F4', borderBottom: '1px solid #FDF0E8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color, background: bg, padding: '2px 8px', borderRadius: 6, border: `1px solid ${border}` }}>
                  {row.order_id}
                </span>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>{row.batch_name}</span>
                {row.order_status && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em',
                    background: row.order_status === 'approved' ? '#F0FDF4' : row.order_status === 'draft' ? '#F9FAFB' : '#FEF3EE',
                    color:      row.order_status === 'approved' ? '#15803D' : row.order_status === 'draft' ? '#6B7280' : '#C84B0F',
                    border:     `1px solid ${row.order_status === 'approved' ? '#BBF7D0' : row.order_status === 'draft' ? '#E5E7EB' : '#FDCFB5'}`,
                  }}>
                    {row.order_status.replace('_', ' ')}
                  </span>
                )}
              </div>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{fmt(row.created_at)}</span>
            </div>
            <div style={{ padding: '13px 16px', display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 2 }}>TOTAL</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1C0A00' }}>{fmtNum(row.total_codes)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4 }}>LOGIN 1</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#15803D', marginBottom: 3 }}>{fmtNum(row.login1_done)}</div>
                <Bar value={row.login1_done} total={row.total_codes} color="#22C55E" />
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4 }}>LOGIN 2</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F766E', marginBottom: 3 }}>{fmtNum(row.login2_done)}</div>
                <Bar value={row.login2_done} total={row.total_codes} color="#14B8A6" />
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4 }}>REMAINING</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#B91C1C', marginBottom: 3 }}>{fmtNum(row.not_redeemed)}</div>
                <Bar value={row.not_redeemed} total={row.total_codes} color="#EF4444" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function OrdersPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/school-portal/orders')
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totals = data ? (() => {
    const all = [...(data.batches || []), ...(data.printCodes || []), ...(data.legacy || [])];
    return all.reduce((a, r) => ({
      total:  a.total  + Number(r.total_codes),
      login1: a.login1 + Number(r.login1_done),
      login2: a.login2 + Number(r.login2_done),
    }), { total: 0, login1: 0, login2: 0 });
  })() : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Package size={22} color="#C84B0F" />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C0A00', margin: 0 }}>Orders</h1>
      </div>
      <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 24 }}>All code batches assigned to your school</p>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12, color: '#9CA3AF' }}>
          <div className="loader" /> Loading orders…
        </div>
      ) : !data ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Failed to load</div>
      ) : (
        <>
          {/* Summary */}
          {totals && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
              {[
                { label: 'Total Codes',   value: totals.total,  color: '#C84B0F', bg: '#FEF3EE', border: '#FDCFB5' },
                { label: 'Login 1 Done',  value: totals.login1, color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
                { label: 'Login 2 Done',  value: totals.login2, color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
              ].map(m => (
                <div key={m.label} style={{ background: m.bg, border: `1px solid ${m.border}`, borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: m.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, opacity: 0.8 }}>{m.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(m.value)}</div>
                </div>
              ))}
            </div>
          )}

          <OrderSection title="Direct Batch Orders" icon={Hash}    color="#C84B0F" bg="#FEF3EE" border="#FDCFB5" rows={data.batches}    idLabel="Batch ID" />
          <OrderSection title="Print Code Orders"   icon={Tag}     color="#1D4ED8" bg="#EEF2FF" border="#BFDBFE" rows={data.printCodes} idLabel="Print Code" />
          <OrderSection title="Legacy Orders"       icon={Package} color="#6B7280" bg="#F9FAFB" border="#E5E7EB" rows={data.legacy}     idLabel="Batch" />

          {!data.batches?.length && !data.printCodes?.length && !data.legacy?.length && (
            <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
              <Package size={40} color="#F0DCC8" style={{ marginBottom: 14 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: '#6B4422' }}>No orders yet</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
